// src/components/Dashboard/PerformanceTab.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button, Chip } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const CHART_COLORS = {
  primary: '#8884d8',
  secondary: '#82ca9d',
  accent: '#ffc658',
  danger: '#ff7c7c',
  success: '#4ade80',
  warning: '#f59e0b'
};

/* ✅ MESMO ESTILO DE BORDA/DEGRADÊ QUE VOCÊ MANDOU */
const gradientCardSx = {
  width: '100%',
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  borderLeft: '3px solid transparent',
  backgroundImage: `
    linear-gradient(#fff, #fff),
    linear-gradient(180deg, #ec4899, #a855f7)
  `,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  backgroundColor: '#fff',
};

const EmojiIcon = ({ children, size = 18 }) => (
  <span style={{ fontSize: size, lineHeight: 1 }}>{children}</span>
);

export const PerformanceTab = ({ performanceOverview, loading, fetchData }) => {
  // ✅ Hooks sempre no topo (pra não quebrar regra do React Hooks)
  const warningTitles = performanceOverview?.warnings_titles || [];
  const [warningIndex, setWarningIndex] = useState(0);
  const dragRef = useRef({ startX: null, dragging: false });

  useEffect(() => {
    if (warningIndex > 0 && warningIndex >= warningTitles.length) {
      setWarningIndex(0);
    }
  }, [warningTitles.length, warningIndex]);

  const currentWarning = useMemo(() => {
    if (!warningTitles || warningTitles.length === 0) return null;
    return warningTitles[warningIndex] || null;
  }, [warningTitles, warningIndex]);

  const goPrevWarning = () => {
    if (!warningTitles || warningTitles.length <= 1) return;
    setWarningIndex((prev) => (prev - 1 + warningTitles.length) % warningTitles.length);
  };

  const goNextWarning = () => {
    if (!warningTitles || warningTitles.length <= 1) return;
    setWarningIndex((prev) => (prev + 1) % warningTitles.length);
  };

  const onWarningsPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, dragging: true };
  };

  const onWarningsPointerUp = (e) => {
    if (!dragRef.current.dragging || dragRef.current.startX === null) return;
    const dx = e.clientX - dragRef.current.startX;

    if (dx > 35) goPrevWarning();
    else if (dx < -35) goNextWarning();

    dragRef.current = { startX: null, dragging: false };
  };

  const onWarningsPointerLeave = () => {
    dragRef.current = { startX: null, dragging: false };
  };

  // ✅ Agora sim: returns condicionais
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!performanceOverview) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 1 }}>
        <Alert severity="error" icon={<EmojiIcon size={18}>💥</EmojiIcon>}>
          Could not load performance data.
        </Alert>
        <Button
          onClick={fetchData}
          variant="outlined"
          startIcon={<EmojiIcon size={16}>🔄</EmojiIcon>}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  const { punctuality, sales, tips } = performanceOverview;

  const getScoreColor = (score) => {
    if (score >= 85) return CHART_COLORS.success;
    if (score >= 70) return CHART_COLORS.warning;
    return CHART_COLORS.danger;
  };

  const renderPunctualityBar = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayAbbrev = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', borderRadius: 2, overflow: 'hidden', height: 40, border: '2px solid #e0e0e0' }}>
          {days.map((day, index) => {
            const dayData = punctuality?.days_data?.find(d => d.day_of_week === day);
            const status = dayData?.status || 'OFF';

            let color = '#e0e0e0';
            let textColor = '#666';

            switch (status) {
              case 'SCHEDULED':
                color = '#e3f2fd';
                textColor = '#1565c0';
                break;
              case 'OFF':
                color = '#f0f0f0';
                textColor = '#999';
                break;
              case 'ON_TIME':
                color = CHART_COLORS.success;
                textColor = 'white';
                break;
              case 'LATE':
                color = CHART_COLORS.warning;
                textColor = 'white';
                break;
              case 'ABSENT':
                color = CHART_COLORS.danger;
                textColor = 'white';
                break;
              default:
                break;
            }

            return (
              <Box
                key={day}
                sx={{
                  flex: 1,
                  backgroundColor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: textColor,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRight: index < days.length - 1 ? '1px solid #fff' : 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                {dayAbbrev[index]}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiIcon size={14}>📅</EmojiIcon>
            <Typography variant="caption">Scheduled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiIcon size={14}>✅</EmojiIcon>
            <Typography variant="caption">On-Time</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiIcon size={14}>⚠️</EmojiIcon>
            <Typography variant="caption">Late</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiIcon size={14}>❌</EmojiIcon>
            <Typography variant="caption">Absent</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSalesChart = () => {
    const salesChartData =
      sales?.daily_data
        ?.filter(d => d.status !== 'OFF')
        .map(day => ({
          day: day.day_of_week.slice(0, 3),
          sales: day.total_sales,
          goal: day.daily_goal ?? 0,
          percentage: day.goal_percentage,
          goalMet: day.goal_percentage >= 100
        })) || [];

    return (
      <Box sx={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={salesChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.9} />
                <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="goalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#e0e0e0" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#e0e0e0" stopOpacity={0.4} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis dataKey="day" stroke="#666" />
            <YAxis stroke="#666" />
            <Tooltip
              formatter={(value, name) => {
                const num = typeof value === 'number' ? value : Number(value) || 0;
                const series = String(name || '').toLowerCase();
                const label = series === 'sales' ? 'Sales' : series === 'goal' ? 'Goal' : String(name);
                return [num.toFixed(0), label];
              }}
              labelFormatter={(label) => `Day: ${label}`}
            />
            <Legend />
            <Bar dataKey="goal" fill="url(#goalGradient)" name="Goal" />
            <Bar dataKey="sales" fill="url(#salesGradient)" name="Sales" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderTipsIndicator = () => {
    const tipPercentage = tips?.statistics?.overall_tip_percentage || 0;
    const radius = 120;
    const strokeWidth = 16;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${(tipPercentage / 15) * circumference} ${circumference}`;

    let color = CHART_COLORS.danger;
    let label = 'Low';
    if (tipPercentage >= 13) {
      color = CHART_COLORS.success;
      label = 'Excellent';
    } else if (tipPercentage >= 9) {
      color = CHART_COLORS.primary;
      label = 'Very Good';
    } else if (tipPercentage >= 6) {
      color = CHART_COLORS.warning;
      label = 'Good';
    } else if (tipPercentage >= 3) {
      color = '#ff9800';
      label = 'Fair';
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
            <circle
              stroke="#e0e0e0"
              fill="transparent"
              strokeWidth={strokeWidth}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            <circle
              stroke={color}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeLinecap="round"
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              style={{ transition: 'stroke-dasharray 0.5s ease-in-out' }}
            />
          </svg>

          <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>
              {tipPercentage.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Week's total: ${tips?.statistics?.total_tips_week || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ideal goal: 5-10% of sales
          </Typography>
        </Box>
      </Box>
    );
  };

  // ✅ helper visual: chip cor por severidade
  const warningChipColor = (sev) => {
    const s = String(sev || 'WARNING').toUpperCase();
    if (s === 'SERIOUS') return CHART_COLORS.danger;
    if (s === 'INFO') return CHART_COLORS.primary;
    return CHART_COLORS.warning;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: '650px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {/* Weekly Summary */}
        <Card sx={gradientCardSx}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 'bold', width: '40%', mb: 1 }}>
              <EmojiIcon>📊</EmojiIcon> WEEKLY SUMMARY
            </Typography>

            <Box display="flex" justifyContent="center" gap={1} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1, textAlign: 'center', p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: CHART_COLORS.success, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  ${sales?.statistics?.total_sales_week || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                >
                  <EmojiIcon size={12}>💵</EmojiIcon> Total Sales
                </Typography>
              </Box>

              <Box sx={{ flex: 1, textAlign: 'center', p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: CHART_COLORS.accent, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  ${tips?.statistics?.total_tips_week || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                >
                  <EmojiIcon size={12}>💰</EmojiIcon> Total Tips
                </Typography>
              </Box>

              <Box sx={{ flex: 1, textAlign: 'center', p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: CHART_COLORS.primary, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  {punctuality?.statistics?.on_time_days || 0}/{punctuality?.statistics?.total_work_days || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                >
                  <EmojiIcon size={12}>✅</EmojiIcon> On-Time Days
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ✅ NEW: Warnings (title-only) */}
        <Card sx={gradientCardSx}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontWeight: 'bold', width: '40%' }}>
                <EmojiIcon>⚠️</EmojiIcon> WARNINGS
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Button
                  onClick={goPrevWarning}
                  variant="outlined"
                  size="small"
                  disabled={!warningTitles || warningTitles.length <= 1}
                  sx={{ minWidth: 38, px: 1, py: 0.25, borderRadius: 2 }}
                >
                  ◀
                </Button>
                <Button
                  onClick={goNextWarning}
                  variant="outlined"
                  size="small"
                  disabled={!warningTitles || warningTitles.length <= 1}
                  sx={{ minWidth: 38, px: 1, py: 0.25, borderRadius: 2 }}
                >
                  ▶
                </Button>
              </Box>
            </Box>

            <Box
              onPointerDown={onWarningsPointerDown}
              onPointerUp={onWarningsPointerUp}
              onPointerLeave={onWarningsPointerLeave}
              sx={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',

                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                p: 1.25,
                border: '1px solid rgba(0,0,0,0.04)',

                userSelect: 'none',
                touchAction: 'pan-y',
                cursor: warningTitles && warningTitles.length > 1 ? 'grab' : 'default'
              }}
            >
              {!currentWarning ? (
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2 }}>
                  ✅ No warnings yet
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={currentWarning.title}
                    >
                      {currentWarning.title}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {currentWarning.created_at ? new Date(currentWarning.created_at).toLocaleDateString() : ''}
                    </Typography>
                  </Box>

                  <Chip
                    label={(currentWarning.severity || 'WARNING').toUpperCase()}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: warningChipColor(currentWarning.severity),
                      color: '#fff'
                    }}
                  />
                </Box>
              )}

              {warningTitles && warningTitles.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1 }}>
                  {warningTitles.map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        backgroundColor: idx === warningIndex ? '#111827' : 'rgba(17, 24, 39, 0.20)'
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Punctuality */}
        <Card sx={gradientCardSx}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 'bold', width: '40%' }}>
                <EmojiIcon>⏱️</EmojiIcon> PUNCTUALITY
              </Typography>
              <Chip
                label={`${punctuality?.statistics?.punctuality_score?.toFixed(1) || 0}%`}
                sx={{
                  backgroundColor: getScoreColor(punctuality?.statistics?.punctuality_score),
                  color: '#fff',
                  fontWeight: 'bold'
                }}
              />
            </Box>

            {renderPunctualityBar()}

            <Alert
              severity={
                punctuality?.statistics?.consecutive_delays > 2 ? 'error' :
                punctuality?.statistics?.consecutive_delays > 0 ? 'warning' : 'success'
              }
              icon={
                punctuality?.statistics?.consecutive_delays > 2 ? <EmojiIcon size={18}>❌</EmojiIcon> :
                punctuality?.statistics?.consecutive_delays > 0 ? <EmojiIcon size={18}>⚠️</EmojiIcon> :
                <EmojiIcon size={18}>✅</EmojiIcon>
              }
              sx={{ fontSize: '0.875rem', mt: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {punctuality?.statistics?.consecutive_delays === 0
                  ? "Congratulations! You haven't been late this week!"
                  : `${punctuality?.statistics?.consecutive_delays} consecutive day(s) late`}
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Sales Goals */}
        <Card sx={gradientCardSx}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 'bold', width: '40%' }}>
                <EmojiIcon>🎯</EmojiIcon> SALES GOALS
              </Typography>
              <Chip
                label={`${sales?.statistics?.weekly_goal_percentage?.toFixed(1) || 0}%`}
                sx={{
                  backgroundColor: getScoreColor(sales?.statistics?.weekly_goal_percentage),
                  color: '#fff',
                  fontWeight: 'bold'
                }}
              />
            </Box>

            {renderSalesChart()}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Goals are dynamic (configured or auto-calculated)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Goals met: {sales?.statistics?.goals_achieved || 0}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Tip Performance */}
        <Card sx={gradientCardSx}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 'bold', width: '40%' }}>
              <EmojiIcon>💸</EmojiIcon> TIP PERFORMANCE
            </Typography>
            {renderTipsIndicator()}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
